import { useIsDesktopTargetSelectorEnabled } from '@/features/flags/desktop-target-selector-flag';
import { QueryClientContext, useQuery } from '@tanstack/react-query';
import { Cloud } from 'lucide-react';
import { useContext, useState } from 'react';

import {
  DESKTOP_TARGETS_QUERY_KEY,
  desktopTargetsClient,
} from '@/features/rpa/desktop-target/desktopTargets';
import {
  CloudComputerExpandButton,
  CloudComputerLightbox,
} from './CloudComputerLightbox';
import { CloudComputerLiveView } from './CloudComputerLiveView';

import {
  classifyCuaCloudProviderState,
  CUA_CLOUD_MACHINE_QUERY_KEY,
  cuaCloudClient,
} from './cuaCloud';

/**
 * Inline live view rendered inside a running computer-use task card. Shows the
 * cloud VM's screen while the agent drives it, so the user watches the work
 * happen in the transcript. Renders nothing unless the cloud machine is the
 * running target — native desktop runs have no server-side VNC to embed.
 *
 * Collapsed by default (a thin "Watch live" bar) so the transcript stays calm;
 * expanding mounts the VNC iframe, which auto-connects and self-reconnects.
 */
export const CloudComputerInlineLiveView = ({
  childAgentRunId,
}: {
  /** Run id used to find which roster target this run is driving; without
   *  it we cannot prove the target is the cloud machine, so we render
   *  nothing. */
  childAgentRunId?: string;
}) => {
  // Hosts without a React Query provider (some standalone windows, unit
  // tests) simply skip the live view rather than crash the whole card.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  if (!hasQueryClient) return null;
  return <InlineLiveViewBody childAgentRunId={childAgentRunId} />;
};

const InlineLiveViewBody = ({ childAgentRunId }: { childAgentRunId?: string }) => {
  const enabled = useIsDesktopTargetSelectorEnabled();
  const [expanded, setExpanded] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // The run's target is the roster row whose assignment slot this run
  // holds. Only a cloud row has a server-side screen to embed — local
  // computer runs must not mount the VNC view or poll machine details.
  // Shares the roster cache entry with useComputerUseQueuedTarget (same key
  // + queryFn), so concurrent observers coalesce into one fetch. Poll fast
  // until the backend records this run's assignment (the row can lag the
  // card mount), then relax.
  const targetsQuery = useQuery({
    queryKey: DESKTOP_TARGETS_QUERY_KEY,
    queryFn: async () => (await desktopTargetsClient.list()).data,
    enabled: enabled && Boolean(childAgentRunId),
    refetchInterval: (query) =>
      query.state.data?.targets.some(
        (target) => target.busy_agent_run_id === childAgentRunId
      )
        ? 15_000
        : 5_000,
  });
  const runTarget = targetsQuery.data?.targets.find(
    (target) => target.busy_agent_run_id && target.busy_agent_run_id === childAgentRunId
  );
  const isCloudRun = runTarget?.is_cloud === true;

  const detailsQuery = useQuery({
    queryKey: CUA_CLOUD_MACHINE_QUERY_KEY,
    queryFn: async () => (await cuaCloudClient.getMachineDetails()).data,
    enabled: enabled && isCloudRun,
    retry: false,
    refetchInterval: 15_000,
  });

  if (!enabled || !isCloudRun) return null;

  const kind = classifyCuaCloudProviderState(detailsQuery.data?.provider_state);
  if (detailsQuery.data && kind === 'paused') return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background-tertiary/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary"
      >
        <span className="flex items-center gap-2">
          <Cloud className="size-3.5" />
          Your cloud resident
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-green-600">
            <span className="size-1.5 animate-pulse rounded-full bg-green-500" />
            {kind === 'running' ? 'Live' : 'Starting'}
          </span>
        </span>
        <span className="text-[11px] text-text-tertiary">
          {expanded ? 'Hide' : 'Watch live'}
        </span>
      </button>
      {expanded && (
        <div className="relative">
          <CloudComputerLiveView className="aspect-video border-t border-border" />
          <CloudComputerExpandButton onClick={() => setLightboxOpen(true)} />
        </div>
      )}
      {/* The inline card is a watch surface, so its lightbox stays
          watch-only; interacting happens through the login clarification
          card's take-over flow. */}
      <CloudComputerLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        interactive={false}
      />
    </div>
  );
};
